<template>
  <div class="flex flex-col w-full h-full overflow-y-auto">
    <!-- Example banner -->
    <div class="m-4 mb-3 p-3 rounded-lg bg-primary-50 text-primary-700 text-sm flex items-center gap-2">
      <i class="pi pi-info-circle" />
      <span>כרטיס לקוח לדוגמה — ככה ייראה כרטיס אמיתי כשיהיו לכם לקוחות.</span>
    </div>

    <!-- Header (mirrors ProfileHeader.vue) -->
    <div class="px-4">
      <div class="flex items-center gap-4 mt-1 w-full">
        <Avatar
          :label="getTwoChars(customer.fullName)"
          shape="circle"
          size="xlarge"
          class="flex-none"
          :style="{ backgroundColor: '#8864ee', color: '#fff' }"
        />
        <div class="flex flex-col flex-grow">
          <div class="flex items-center gap-2">
            <span class="text-2xl font-medium">{{ customer.fullName }}</span>
            <span class="mx-1">○</span>
            <div class="flex flex-nowrap overflow-x-auto gap-2">
              <Badge v-for="tag of customer.tags" :key="tag" :value="tag" severity="contrast" class="flex-none" />
            </div>
          </div>
          <span class="text-sm text-gray-500">{{ profileSubTitle }}</span>
        </div>
      </div>
    </div>

    <!-- Tabs (real TabsComponent) -->
    <div class="px-4 mt-4">
      <TabsComponent v-model="activeTab" :tabs="tabs" />
    </div>

    <!-- Content -->
    <div class="px-4 py-4 flex-1">
      <!-- ── Appointments (mirrors CustomersAppointmentTimeline.vue) ── -->
      <div v-if="activeTab === 'appointments'" class="w-full flex flex-col gap-4">
        <div v-for="appt in appointments" :key="appt.id" class="border p-3 rounded flex flex-col gap-3"
             :class="{ 'bg-orange-50': appt.status === 'no_show' }">
          <!-- service row -->
          <div class="flex gap-3 items-center">
            <div class="w-12 h-12 rounded-lg flex-none" :style="{ background: appt.color }" />
            <div class="flex flex-col">
              <span class="font-medium text-sm">{{ appt.service }}</span>
              <span class="text-xs text-gray-500">{{ appt.duration }} · ₪{{ appt.price }}</span>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-2 w-full">
            <div class="flex flex-col w-full">
              <span class="text-xs text-gray-500">תאריך ושעה</span>
              <span class="text-sm">{{ appt.date }}</span>
            </div>
            <div class="flex flex-col w-full">
              <span class="text-xs text-gray-500">עובד/ת</span>
              <span class="text-sm">{{ customer.providerName }}</span>
            </div>
          </div>
          <div v-if="appt.note" class="flex flex-col w-full">
            <span class="text-xs text-gray-500">הערות</span>
            <span class="text-sm">{{ appt.note }}</span>
          </div>
          <div class="flex flex-col w-full gap-2">
            <span class="text-xs text-gray-500">סטטוס</span>
            <span v-if="appt.status === 'done'"
                  class="text-left text-sm text-green-500 flex gap-1 items-center px-3 py-1 rounded-lg bg-green-50 w-max h-min">
              <i class="pi pi-check" style="font-size: 0.75rem" /><span>תור הושלם</span>
            </span>
            <span v-else-if="appt.status === 'no_show'"
                  class="text-left text-sm text-orange-500 flex gap-1 items-center px-3 py-1 rounded-lg bg-orange-50 w-max h-min">
              <i class="pi pi-user-minus" style="font-size: 0.75rem" /><span>לא הגיע/ה</span>
            </span>
            <span v-else
                  class="text-left text-sm text-blue-500 flex gap-1 items-center px-3 py-1 rounded-lg bg-blue-50 w-max h-min">
              <span>תור עתידי</span>
            </span>
          </div>
        </div>
      </div>

      <!-- ── About (mirrors CustomerInformation.vue) ── -->
      <div v-else-if="activeTab === 'information'" class="flex flex-col gap-2 w-full">
        <!-- Birthday banner -->
        <div class="mb-4 w-full flex flex-col gap-2 rounded-lg border bg-gradient-to-bl from-gray-50 via-primary-100 to-primary-300 relative pr-4 py-6 pl-6">
          <span class="font-medium">יום ההולדת יחול החודש!</span>
          <span class="text-sm text-gray-700">ללקוח/ה יש החודש יום הולדת בתאריך 14 בחודש, תמסרו מזל טוב גם מאיתנו :)</span>
        </div>

        <div class="w-full flex flex-col gap-1">
          <span class="font-medium">שם הלקוח</span>
          <span class="text-gray-800">{{ customer.fullName }}</span>
        </div>
        <Divider />

        <div class="w-full flex flex-col gap-2">
          <div class="flex justify-between items-center w-full">
            <span class="font-medium">פרטי יצירת קשר</span>
            <div class="flex gap-2 items-center">
              <span class="rounded-full flex items-center justify-center p-2 border border-green-500 bg-green-50">
                <i class="pi pi-whatsapp text-2xl" />
              </span>
              <span class="rounded-full flex items-center justify-center p-2 border border-blue-500 bg-blue-50">
                <i class="pi pi-phone text-2xl" />
              </span>
            </div>
          </div>
          <div class="flex relative w-full gap-3">
            <span class="text-left text-sm text-primary-500 border border-primary-500 flex gap-1 items-center px-2 rounded bg-primary-50">
              <span>{{ formattedPhone }}</span><i class="pi pi-phone" style="font-size: 0.5rem" />
            </span>
            <span class="text-left text-sm text-primary-500 border border-primary-500 flex gap-1 items-center px-2 rounded bg-primary-50">
              <span>{{ customer.email }}</span><i class="pi pi-at" style="font-size: 0.5rem" />
            </span>
          </div>
        </div>
        <Divider />

        <div class="w-full flex flex-none gap-3 justify-start items-center">
          <i class="rounded-full p-2.5 pi text-gray-500 bg-gray-100 pi-tags" style="font-size: 0.85rem;" />
          <div class="w-full flex flex-col gap-1 items-start">
            <span class="font-medium">תגיות</span>
            <div class="flex gap-2">
              <Badge v-for="tag of customer.tags" :key="tag" :value="tag" severity="contrast" />
            </div>
          </div>
        </div>

        <div class="w-full flex gap-3 flex-none mt-4 justify-start items-start">
          <i class="rounded-full p-2.5 pi text-gray-500 bg-gray-100 pi-file" style="font-size: 0.85rem;" />
          <div class="flex flex-col gap-1 items-start">
            <span class="font-medium">הערה כללית</span>
            <span class="text-xs pe-6 text-gray-500 whitespace-pre-wrap">{{ customer.internalNote }}</span>
          </div>
        </div>

        <div class="w-full flex gap-3 mt-4 flex-none justify-start items-start">
          <i class="rounded-full p-2.5 pi text-red-500 bg-red-50 pi-exclamation-triangle" style="font-size: 0.85rem;" />
          <div class="flex flex-col gap-1 items-start">
            <span class="font-medium">הערות חשובות</span>
            <span class="text-xs pe-6 whitespace-pre-wrap text-red-600">{{ customer.warningNote }}</span>
          </div>
        </div>

        <div class="w-full flex gap-3 mt-4 flex-none justify-start items-center">
          <i class="rounded-full p-2.5 pi text-gray-500 bg-gray-100 pi-palette" style="font-size: 0.85rem;" />
          <div class="flex flex-col gap-1 items-start">
            <span class="font-medium">סימון לקוח</span>
            <span class="text-xs pe-6 text-gray-500">תווית: לקוחה קבועה</span>
          </div>
        </div>
      </div>

      <!-- ── Loyalty ── -->
      <div v-else-if="activeTab === 'loyalty-program'">
        <div class="rounded-2xl p-5 text-white bg-gradient-to-l from-primary-600 to-primary-400">
          <div class="flex items-center gap-2 mb-2">
            <i class="pi pi-star-fill" /><span class="font-medium">מועדון הלקוחות</span>
          </div>
          <div class="text-2xl font-bold mb-1">8 / 10 ביקורים</div>
          <div class="w-full bg-white/30 rounded-full h-2 overflow-hidden">
            <div class="bg-white h-2 rounded-full" style="width: 80%" />
          </div>
          <div class="text-xs mt-2 opacity-90">עוד 2 ביקורים והלקוחה מקבלת הטבה 🎁</div>
        </div>
      </div>

      <!-- ── Payments ── -->
      <div v-else class="flex flex-col gap-3">
        <div class="flex justify-between items-center bg-surface-50 rounded-xl p-3">
          <span class="text-sm text-gray-600">סה״כ הכנסות מהלקוחה</span>
          <span class="font-bold">₪1,840</span>
        </div>
        <div v-for="appt in paidAppointments" :key="appt.id"
             class="flex items-center justify-between p-3 rounded-xl border bg-white text-sm">
          <div class="flex flex-col">
            <span class="font-medium">{{ appt.service }}</span>
            <span class="text-xs text-gray-500">{{ appt.date }}</span>
          </div>
          <span class="text-green-600 font-medium">₪{{ appt.price }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import Avatar from 'primevue/avatar';
import Badge from 'primevue/badge';
import Divider from 'primevue/divider';
import { TabsComponent } from '@torim-app/shared-components';
import { getTwoChars } from '@torim-app/utils';

type Tab = 'appointments' | 'information' | 'loyalty-program' | 'payments';
const activeTab = ref<Tab>('appointments');

const tabs: Array<{ label: string; value: Tab }> = [
  { label: 'תורים', value: 'appointments' },
  { label: 'אודות', value: 'information' },
  { label: 'מועדון לקוחות', value: 'loyalty-program' },
  { label: 'תשלומים', value: 'payments' },
];

const customer = {
  fullName: 'דנה לוי',
  phoneNumber: '0501234567',
  email: 'dana.levi@example.com',
  tags: ['לקוחה קבועה', 'צבע'],
  internalNote: 'מעדיפה תורים בשעות הבוקר.',
  warningNote: 'אלרגיה לאמוניה — להשתמש בצבע ללא אמוניה.',
  providerName: 'את/ה',
};

const profileSubTitle = 'לקוחה מאז מרץ 2024';

const formattedPhone = `${customer.phoneNumber.substring(0, 3)}-${customer.phoneNumber.substring(3, 6)}-${customer.phoneNumber.substring(6, 10)}`;

type ApptStatus = 'done' | 'no_show' | 'upcoming';
const appointments: Array<{ id: string; date: string; service: string; duration: string; price: number; color: string; status: ApptStatus; note?: string }> = [
  { id: '1', date: '20.06.2025 · 10:00', service: 'תספורת', duration: '45 דקות', price: 90, color: '#8864ee', status: 'upcoming' },
  { id: '2', date: '03.07.2025 · 16:30', service: 'צבע ופן', duration: '90 דקות', price: 260, color: '#22c55e', status: 'upcoming' },
  { id: '3', date: '12.05.2025 · 11:00', service: 'תספורת + פן', duration: '60 דקות', price: 180, color: '#8864ee', status: 'done', note: 'הוסיפה טיפול קרטין בפעם הבאה.' },
  { id: '4', date: '28.04.2025 · 09:30', service: 'צבע', duration: '75 דקות', price: 260, color: '#22c55e', status: 'done' },
  { id: '5', date: '02.04.2025 · 14:00', service: 'תספורת', duration: '45 דקות', price: 90, color: '#8864ee', status: 'no_show' },
];

const paidAppointments = appointments.filter((a) => a.status === 'done');
</script>
